import React from "react";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";

/**
 * Reusable Excel-like sortable table header.
 *
 * Props:
 *  - k: string — column key
 *  - sortKey, sortDir: current sort state from parent
 *  - onSort(k): cycle sort callback
 *  - align: "left" | "right" | "center"
 *  - className: extra classes
 *  - children: header label
 */
export default function SortHeader({ k, sortKey, sortDir, onSort, align = "left", className = "", children }) {
  const isActive = sortKey === k;
  const Icon = !isActive ? ChevronsUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
  const iconClass = isActive ? "" : "opacity-40";
  return (
    <th
      className={`py-2 px-2 label cursor-pointer select-none hover:bg-[var(--surface-3)] ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"} ${className}`}
      onClick={() => onSort(k)}
      data-testid={`sort-header-${k}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "flex-row-reverse" : ""}`}>
        {children}
        <Icon size={11} strokeWidth={2.5} className={iconClass} />
      </span>
    </th>
  );
}

/**
 * Hook helper to manage tri-state sort cycle: asc → desc → none.
 */
export function useSort(initialKey = null) {
  const [sortKey, setSortKey] = React.useState(initialKey);
  const [sortDir, setSortDir] = React.useState("asc");
  const cycleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
  };
  return { sortKey, sortDir, cycleSort };
}
